/**
 * Column + grid configuration for <app-data-grid>.
 *
 * Field names deliberately mirror DevExtreme's `dxi-column` API (dataField,
 * caption, alignment, width, cellTemplate, groupIndex, ...). 136 of the 239
 * grids in this app already build their columns from a TypeScript array and
 * render them with `<dxi-column *ngFor="let f of columnDefs" ...>`, so keeping
 * the names identical means those arrays move across untouched and the
 * conversion is a template swap rather than a rewrite.
 */
export type GridAlign = 'left' | 'center' | 'right';

export interface GridColumn {
  /** Property on the row object. Omit only for pure template columns. */
  dataField?: string;
  /** Header text. */
  caption?: string;
  alignment?: GridAlign;
  /** Number (px) or any CSS width. */
  width?: number | string;
  minWidth?: number;
  /** false hides the column (still listed in the column chooser). */
  visible?: boolean;
  /** Hide from the column chooser entirely (e.g. an action button column). */
  showInColumnChooser?: boolean;
  allowSorting?: boolean;
  /** Filter ROW editor for this column. */
  allowFiltering?: boolean;
  /**
   * Header filter DROPDOWN. DevExtreme kept this separate from allowFiltering,
   * and `custom-grid` drives the two off different config flags, so collapsing
   * them would turn one screen's settings into the wrong UI.
   */
  allowHeaderFiltering?: boolean;
  /** Whether the search panel looks in this column. Defaults to true. */
  allowSearch?: boolean;
  allowResizing?: boolean;
  allowHiding?: boolean;
  allowGrouping?: boolean;
  /** false keeps the cell read-only while its row is being edited. */
  allowEditing?: boolean;
  /** Excluded from the Excel export when false. */
  allowExporting?: boolean;
  /** Pins the column; 'left' unless fixedPosition says otherwise. */
  fixed?: boolean;
  fixedPosition?: 'left' | 'right';
  /** Drives filter-row editor + sort comparator + export cell type. */
  dataType?: 'string' | 'number' | 'date' | 'boolean';
  /** Group by this column on first render; lower index groups first. */
  groupIndex?: number;
  sortOrder?: 'asc' | 'desc';
  sortIndex?: number;
  /** Name of an <ng-template appGridCell="..."> supplied by the host. */
  cellTemplate?: string;
  /** Name of an <ng-template appGridHeaderCell="..."> supplied by the host. */
  headerCellTemplate?: string;
  cssClass?: string;
  /** Derives a value instead of reading dataField. Used for sort/filter/export too. */
  calculateCellValue?: (row: any) => any;
  /** Display-only formatter. Export uses the RAW value so Excel keeps its type. */
  format?: GridFormat;
  /**
   * Arbitrary payload carried to the cell template as `column.meta`.
   *
   * For columns generated by an *ngFor whose cell template needs the loop item.
   * custom-grid renders one action column per configured row button and the
   * template needs that button's label/icon/handler. Putting the template inside
   * the *ngFor instead would bury it in an embedded view, where the grid's
   * @ContentChildren (descendants: false) cannot see it - and widening that
   * query would make every master-detail grid start picking up its child grid's
   * templates.
   */
  meta?: any;
  /**
   * `<dxo-lookup>`: the cell stores an id, but shows (and edits) a label from a
   * reference list.
   *
   *   <dxi-column dataField="ise_type_id">
   *     <dxo-lookup [dataSource]="asstIssueTypes"
   *                 displayExpr="ise_type_nm" valueExpr="ise_type_id">
   *
   * Without this the column renders a bare numeric id and the row editor offers
   * a free-text box over a foreign key.
   */
  lookup?: {
    /**
     * Array, or a function returning one.
     *
     * Prefer the FUNCTION form whenever the reference list is loaded by its own
     * API call. In asset-issues the lookup source (`asstIssueTypes`) and the
     * column definitions are built in two different callbacks with no ordering
     * between them, so a captured array reference is either undefined or goes
     * stale the moment the property is reassigned - and the column then renders
     * raw foreign-key ids forever, with no error.
     */
    dataSource: any[] | (() => any[]);
    displayExpr: string;
    valueExpr: string;
  };
}

/** A footer aggregate, mirroring DevExtreme's `dxi-total-item`. */
export interface GridTotalItem {
  column: string;
  /**
   * Render the value under a DIFFERENT column, mirroring dxi-total-item's
   * showInColumn. Without it a summary configured to sit under a label column
   * silently moves to the column it aggregates.
   */
  showInColumn?: string;
  summaryType: 'sum' | 'count' | 'avg' | 'min' | 'max';
  /** e.g. 'Total: {0}' */
  displayFormat?: string;
  /**
   * Same union as a column's format - the summary arrays in the enterprise
   * dashboards pass `{ type: 'fixedPoint', precision: 2 }`, not a function.
   * summaryValue() used to call this unconditionally as `valueFormat(v)`, which
   * threw at render for every one of them.
   */
  valueFormat?: GridFormat;
}

/**
 * Per-GROUP aggregate, mirroring `dxi-group-item` with `[showInGroupFooter]="true"`
 * and `[alignByColumn]="true"` - a footer row after each group's rows, each value
 * sitting under its own column.
 *
 * Distinct from GridTotalItem, which is one footer row for the whole grid.
 */
export interface GridGroupItem {
  column: string;
  summaryType: 'sum' | 'count' | 'avg' | 'min' | 'max';
  /** e.g. 'Total Trips: {0}'. Defaults to the bare value. */
  displayFormat?: string;
  /** Same union as a column's format; see GridTotalItem.valueFormat. */
  valueFormat?: GridFormat;
}

/**
 * In-grid editing, mirroring `<dxo-editing>`.
 *
 * Only `mode: 'row'` is implemented, because that is all the app uses. Of the 15
 * grids declaring <dxo-editing>, TWELVE set allowUpdating/allowAdding/allowDeleting
 * all false - editing declared but disabled - and the three live ones are all
 * row mode. `allowAdding` is accepted for shape but is false everywhere today.
 */
export interface GridEditing {
  mode?: 'row';
  allowUpdating?: boolean;
  allowAdding?: boolean;
  allowDeleting?: boolean;
  /** Confirmation text for delete. Set to null to delete without asking. */
  confirmDeleteMessage?: string | null;
}

/**
 * Payload of the `saved` output. Deliberately DevExtreme's `onSaved` shape -
 * existing handlers read `e.changes[0].type`, `.key` and `.data`, so they port
 * without edits.
 */
export interface GridSaveEvent {
  changes: Array<{ type: 'update' | 'remove'; key: any; data: any }>;
}

/** Toolbar button, replacing the `onToolbarPreparing` + `widget:'dxButton'` pattern. */
export interface GridToolbarItem {
  text?: string;
  /** ng-zorro icon name, e.g. 'plus', 'reload'. */
  icon?: string;
  hint?: string;
  disabled?: boolean;
  visible?: boolean;
  cssClass?: string;
  onClick: () => void;
}

/**
 * One node of the header tree. A band is a column that has children
 * (DevExtreme's banded column group); a leaf is one that has none. Nesting is
 * unbounded - `custom-grid` goes six deep.
 *
 * `colspan` is the node's leaf count and `rowspan` lets a leaf declared above
 * the deepest level reach down to the bottom of the header, which is what keeps
 * a mixed header (some columns banded, some not) aligned.
 */
export interface GridHeaderNode {
  col: GridColumn;
  children: GridHeaderNode[];
  /** Leaves beneath this node; 1 for a leaf. */
  colspan: number;
  /** 1 for a band, `depth - level` for a leaf. */
  rowspan: number;
}

/**
 * A column's display format, in the three shapes the app already writes.
 *
 * DevExtreme accepted a function, a named-type object, or plain
 * Intl.NumberFormat options, and all three are in use here:
 *
 *   format: this.currencyINRFormater                              // function
 *   format: { type: 'fixedPoint', precision: 2 }                  // named type
 *   [format]="{ style: 'currency', currency: 'INR', ... }"        // Intl options
 *
 * Typing this as a bare function was wrong in a way the compiler could not see:
 * an object reached `col.format(v, row)` and threw at RENDER time, so the build
 * stayed green and the grid went blank. `''` is accepted because the app writes
 * it as the "no format" branch of a ternary.
 */
export type GridFormat =
  | ((value: any, row?: any) => string)
  | ({ type?: 'fixedPoint' | 'percent' | 'currency' | 'decimal'; precision?: number }
     & Intl.NumberFormatOptions)
  | '';
